import { Formik, FormikConfig, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import {
  preferencesCardBodyClassName,
  preferencesCardClassName,
  preferencesCardHeaderClassName,
} from "./layout";
import {
  mailingOptionsFormKeys,
  MailingOptionsFormValues,
  SerializeForm,
  sliceProfileValues,
  type FormValues,
} from "components/ProfileForm/support";
import {
  ProfileFormRadioGroup,
  SubmitButton,
} from "components/ProfileForm/ProfileFormInputs";
import * as API from "lib/api/types";
import { Card, CardTitle } from "@dsaui/components";

interface MailDeliveryPreferencesFormProps {
  profile: FormValues;
  ready: boolean;
  submitProfileUpdate: (payload: API.UserProfilePayload) => Promise<void>;
}

const MailDeliveryPreferencesForm = ({
  profile,
  ready,
  submitProfileUpdate,
}: MailDeliveryPreferencesFormProps) => {
  const { t } = useTranslation();
  const initialValues = sliceProfileValues(mailingOptionsFormKeys, profile);

  const submitForm: FormikConfig<MailingOptionsFormValues>["onSubmit"] = async (
    values,
    { setSubmitting },
  ): Promise<void> => {
    try {
      await submitProfileUpdate(SerializeForm.valuesToMailingPreferences(values));
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik<MailingOptionsFormValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={submitForm}
      validateOnMount
    >
      {(fp: FormikProps<MailingOptionsFormValues>) => (
        <form aria-label="mail delivery preferences form" onSubmit={fp.handleSubmit}>
          <Card className={preferencesCardClassName}>
            <div className={preferencesCardHeaderClassName}>
              <CardTitle>{t("forms.preferences.mailingSection")}</CardTitle>
            </div>
            <div className={preferencesCardBodyClassName}>
              <ProfileFormRadioGroup
                name="mailingPreference"
                options={Object.values(API.MailingPreference)}
                fp={fp}
              />
              <ProfileFormRadioGroup
                name="dlMailingPreference"
                options={Object.values(API.MagazinePreference)}
                fp={fp}
              />
              <div>
                <SubmitButton
                  block
                  data-testid="save-mail-delivery-preferences"
                  fp={fp}
                  ready={ready}
                >
                  {t("save")}
                </SubmitButton>
              </div>
            </div>
          </Card>
        </form>
      )}
    </Formik>
  );
};

export default MailDeliveryPreferencesForm;
